// components/Toast/useToast.js - Add duplicate prevention
import { useState, useCallback } from 'react';

const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, title, options = {}) => {
    const toastId = options.id || `${title}-${message}`.replace(/\s+/g, '-').toLowerCase();
    
    // ✅ Prevent duplicate toasts with same ID
    setToasts(prev => {
      const existingIndex = prev.findIndex(toast => toast.id === toastId);
      if (existingIndex !== -1) {
        console.log("🚫 Preventing duplicate toast:", toastId);
        return prev; // Don't add duplicate
      }
      
      const newToast = {
        id: toastId,
        message,
        title,
        type: options.type || 'info',
        duration: options.duration || 4000,
        timestamp: Date.now()
      };
      
      return [...prev, newToast];
    });
  }, []);

  const showSuccess = useCallback((message, title = 'Success', options = {}) => {
    showToast(message, title, { ...options, type: 'success', id: options.id || `success-${title}` });
  }, [showToast]);

  const showError = useCallback((message, title = 'Error', options = {}) => {
    showToast(message, title, { ...options, type: 'error', id: options.id || `error-${title}` });
  }, [showToast]);

  const showWarning = useCallback((message, title = 'Warning', options = {}) => {
    showToast(message, title, { ...options, type: 'warning', id: options.id || `warning-${title}` });
  }, [showToast]);

  const showInfo = useCallback((message, title = 'Info', options = {}) => {
    showToast(message, title, { ...options, type: 'info', id: options.id || `info-${title}` });
  }, [showToast]);

  const removeToast = useCallback((toastId) => {
    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return {
    toasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeToast,
    clearAllToasts
  };
};

export default useToast;
